import { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft, FaPlus, FaClone, FaChevronDown, FaChevronUp, FaBan, FaCheck,
  FaExclamationTriangle, FaClipboardList,
} from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import SeccionesAdmin from "./SeccionesAdmin";
import Modal from "../Modal";

export default function EncuestasAdmin() {

  const { token } = useContext(AuthContext);

  const [encuestas, setEncuestas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandidoId, setExpandidoId] = useState(null);

  const [nombre, setNombre] = useState("");
  const [anio, setAnio] = useState("");
  const [anioObjetivo, setAnioObjetivo] = useState("");
  const [creando, setCreando] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [encuestaSeleccionada, setEncuestaSeleccionada] = useState(null);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoAnio, setNuevoAnio] = useState("");
  const [nuevoAnioObjetivo, setNuevoAnioObjetivo] = useState("");
  const [clonando, setClonando] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [encuestaEstado, setEncuestaEstado] = useState(null);

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const fetchEncuestas = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch("/api/admin/encuestas", {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      setEncuestas(await res.json());
    } catch (error) {
      mostrarToast("Error cargando encuestas", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEncuestas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const crearEncuesta = async () => {
    if (!nombre.trim() || !anio || !anioObjetivo) {
      mostrarToast("Nombre, año y año objetivo son obligatorios", "error");
      return;
    }

    setCreando(true);
    try {
      const res = await fetch("/api/admin/encuestas", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ nombre, anio, anio_objetivo: anioObjetivo }),
      });

      if (!res.ok) {
        mostrarToast("Error creando la encuesta", "error");
        return;
      }

      setNombre("");
      setAnio("");
      setAnioObjetivo("");
      mostrarToast("Encuesta creada correctamente");
      fetchEncuestas();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    } finally {
      setCreando(false);
    }
  };

  const abrirModalClonar = (encuesta) => {
    setEncuestaSeleccionada(encuesta);
    setNuevoNombre(encuesta.nombre + " copia");
    setNuevoAnio(encuesta.anio + 1);
    setNuevoAnioObjetivo(encuesta.anio_objetivo + 1);
    setModalOpen(true);
  };

  const confirmarClonado = async () => {
    if (!nuevoNombre.trim() || !nuevoAnio || !nuevoAnioObjetivo) {
      mostrarToast("Nombre, año y año objetivo son obligatorios", "error");
      return;
    }

    setClonando(true);
    try {
      const res = await fetch(
        `/api/admin/encuestas/${encuestaSeleccionada.id}/clonar`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            nombre: nuevoNombre,
            anio: Number(nuevoAnio),
            anio_objetivo: Number(nuevoAnioObjetivo),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        mostrarToast(data.error || "Error clonando la encuesta", "error");
        return;
      }

      setModalOpen(false);
      mostrarToast("Encuesta clonada correctamente");
      fetchEncuestas();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    } finally {
      setClonando(false);
    }
  };

  const abrirConfirmEstado = (encuesta) => {
    setEncuestaEstado(encuesta);
    setConfirmOpen(true);
  };

  const confirmarCambioEstado = async () => {
    const activar = !encuestaEstado.activa;
    try {
      const res = await fetch(`/api/admin/encuestas/${encuestaEstado.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...encuestaEstado, activa: activar }),
      });

      if (!res.ok) {
        mostrarToast("Error actualizando la encuesta", "error");
        return;
      }

      mostrarToast(activar ? "Encuesta activada" : "Encuesta desactivada");
      setConfirmOpen(false);
      fetchEncuestas();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    }
  };

  const inputClass =
    "border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";

  return (
    <div className="p-6 bg-slate-50">

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      <div className="mb-6">
        <Link to="/dashboard/administracion" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1 mb-1">
          <FaArrowLeft className="text-xs" /> Administración
        </Link>
        <h1 className="text-2xl font-bold text-slate-800">Encuestas</h1>
        <p className="text-sm text-slate-500">Crea, clona y administra las encuestas de la iglesia.</p>
      </div>

      {/* CREAR */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-slate-800 mb-4">Nueva encuesta</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Nombre</label>
            <input
              placeholder="Nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Año</label>
            <input
              placeholder="Año"
              value={anio}
              onChange={(e) => setAnio(e.target.value)}
              className={`${inputClass} w-28`}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Año objetivo</label>
            <input
              placeholder="Año objetivo"
              value={anioObjetivo}
              onChange={(e) => setAnioObjetivo(e.target.value)}
              className={`${inputClass} w-32`}
            />
          </div>

          <button
            onClick={crearEncuesta}
            disabled={creando}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <FaPlus className="text-xs" /> {creando ? "Creando..." : "Crear"}
          </button>
        </div>
      </div>

      {/* LISTA */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400">
            Cargando encuestas...
          </div>
        ) : encuestas.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400">
            Aún no hay encuestas registradas.
          </div>
        ) : (
          encuestas.map((e) => (
            <div key={e.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="flex flex-wrap justify-between items-center gap-3 p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                    <FaClipboardList className="text-sm" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm text-slate-800 truncate">{e.nombre}</p>
                      <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                        e.activa ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${e.activa ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {e.activa ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{e.anio} → {e.anio_objetivo}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => abrirModalClonar(e)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                  >
                    <FaClone className="text-[10px]" /> Clonar
                  </button>
                  <button
                    onClick={() => abrirConfirmEstado(e)}
                    className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border transition ${
                      e.activa
                        ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                        : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                    }`}
                  >
                    {e.activa ? <><FaBan className="text-[10px]" /> Desactivar</> : <><FaCheck className="text-[10px]" /> Activar</>}
                  </button>
                  <button
                    onClick={() => setExpandidoId(expandidoId === e.id ? null : e.id)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition"
                  >
                    {expandidoId === e.id ? <><FaChevronUp className="text-[10px]" /> Cerrar</> : <><FaChevronDown className="text-[10px]" /> Editar</>}
                  </button>
                </div>
              </div>

              {expandidoId === e.id && (
                <div className="border-t border-slate-100 bg-slate-50/60 p-4">
                  <SeccionesAdmin encuestaId={e.id} />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* MODAL CLONAR */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Clonar encuesta">
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nombre <span className="text-rose-500">*</span>
            </label>
            <input
              value={nuevoNombre}
              onChange={(e) => setNuevoNombre(e.target.value)}
              placeholder="Nombre"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Año</label>
              <input
                value={nuevoAnio}
                onChange={(e) => setNuevoAnio(e.target.value)}
                placeholder="Año"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Año objetivo</label>
              <input
                value={nuevoAnioObjetivo}
                onChange={(e) => setNuevoAnioObjetivo(e.target.value)}
                placeholder="Año objetivo"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-2 pt-4 border-t border-slate-100">
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>

            <button
              onClick={confirmarClonado}
              disabled={clonando}
              className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium transition"
            >
              {clonando ? "Clonando..." : "Clonar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* MODAL CONFIRMAR ACTIVAR/DESACTIVAR */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            encuestaEstado?.activa ? "bg-rose-50 text-rose-500" : "bg-emerald-50 text-emerald-500"
          }`}>
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {encuestaEstado?.activa ? "Desactivar" : "Activar"} encuesta
          </h3>
          <p className="text-sm text-slate-500 mb-6">¿Deseas continuar?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarCambioEstado}
              className={`w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition ${
                encuestaEstado?.activa ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
